// libraries
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// slices.
import { getHermesData, getHermesDataBody } from '../slices/hermesData.slice';
import {
  setWebsocketError,
  setWebsocketLoading,
  setWebsocketResponses,
} from '../slices/webSocketGeoref.slice';

// hooks.
import { useSaveHermesMutation } from '../services/hermes.endpoints';

import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';

interface UseWebSocketGeorefProps {
  whenSuccessGeoref: () => void;
}

const choosePutLeadService = (country: CountryCode | undefined) => {
  switch (country) {
    case 'MX':
      return 'put_lead_mx';
    case 'CO':
      return 'put_lead_co';
    default:
      return 'put_lead_mx';
  }
};

const useWebSocketGeoref = ({ whenSuccessGeoref }: UseWebSocketGeorefProps) => {
  const dispatch = useDispatch();

  const hermesData = useSelector(getHermesData);
  const bodyForHermes = useSelector(getHermesDataBody);

  const country = useSelector(getCountry);
  const georefService = choosePutLeadService(country);

  const [saveHermes] = useSaveHermesMutation();

  const websocketResponsesRef = useRef<any[]>([]);

  const [ws, setWs] = useState<WebSocket | null>(null);

  useEffect(() => {
    const websocket = new WebSocket(
      process.env.ENV_WEBSOCKET_BACKBONE as string
    );
    setWs(websocket);

    return () => {
      websocket.close();
    };
  }, []);

  useEffect(() => {
    if (!ws) return;

    ws.onopen = () => {
      console.log('WebSocket conectado');
    };

    ws.onmessage = async evt => {
      dispatch(setWebsocketLoading(true));

      try {
        console.log('Mensaje recibido:', evt.data);
        const parsedData = JSON.parse(evt.data);
        if (parsedData?.body) {
          const parsedBody = JSON.parse(parsedData.body);

          if (parsedBody?.step === 'georeferencing' && parsedBody?.success) {
            try {
              await saveHermes({
                ...bodyForHermes!,
                validations: false,
              }).unwrap();

              whenSuccessGeoref();
            } catch (e) {
              console.error('Error en segunda llamada (validations=false):', e);
              dispatch(setWebsocketError(true));
            } finally {
              dispatch(setWebsocketLoading(false));
            }
          } else if (
            parsedBody?.step === 'georeferencing' &&
            parsedBody?.success === false
          ) {
            console.log('Error en georreferenciación (WS)');
            dispatch(setWebsocketError(true));
            dispatch(setWebsocketLoading(false));
          }

          websocketResponsesRef.current.push(parsedBody);
        }
      } catch (error) {
        console.error('Error parseando la data del WS:', error);
        dispatch(setWebsocketError(true));
        dispatch(setWebsocketLoading(false));
      }
    };

    ws.onerror = error => {
      console.log('WebSocket error:', error);
      dispatch(setWebsocketError(true));
      dispatch(setWebsocketLoading(false));
    };

    ws.onclose = () => {
      console.log('WebSocket desconectado, reintentando...');
      setWs(new WebSocket(process.env.ENV_WEBSOCKET_BACKBONE as string));
      dispatch(setWebsocketLoading(false));
    };
  }, [ws, dispatch, saveHermes, hermesData, whenSuccessGeoref]);

  const sendMessage = (message: any) => {
    if (!ws) return;
    if (!message.deal_id) return;

    const agente = message.agent || message.agente;

    console.log('Sending message: ', {
      action: georefService,
      Authorization: 'default',
      body: {
        ...message,
        agente,
      },
    });
    const testBody = JSON.stringify({
      action: georefService,
      Authorization: 'default',
      body: JSON.stringify({
        ...message,
        agente,
      }),
    });

    ws.send(testBody);
  };

  useEffect(() => {
    if (hermesData?.deal_id && hermesData?.validations === true) {
      sendMessage(hermesData);
    }
  }, [hermesData]);

  useEffect(() => {
    if (websocketResponsesRef.current.length > 0) {
      dispatch(setWebsocketResponses(websocketResponsesRef.current));
    }
  }, [websocketResponsesRef.current.length, dispatch]);

  return {};
};

export default useWebSocketGeoref;
